/**
 * Orders slice — Redux Toolkit state for the customer-facing orders and
 * quote-requests sub-system.
 *
 * State shape
 * -----------
 * Two primary concerns, each with independent loading + error states so
 * a failure in the orders list never affects the quotes loading state.
 *
 * orders:
 *   - list (EntityAdapter<OrderDTO>) — paginated list of the customer's orders
 *   - pagination — {page, perPage, total, lastPage} metadata from the last fetch
 *   - selectedOrder — full single-order detail (with nested items / history /
 *     discounts / shipments), or null before the first `fetchOrderById`
 *   - tracking — Record<number, OrderTrackingDTO> keyed by order id so
 *     tracking data for multiple orders is cached independently
 *
 * quotes:
 *   - list — simple array of QuoteRequestDTO (with nested quote summary)
 *   - pagination — same shape as orders.pagination
 *   - selectedQuote — full single-quote detail, or null
 *
 * Per-operation loading/error:
 *   - loading.fetchOrders, loading.fetchOrderById, loading.fetchOrderTracking,
 *     loading.fetchQuotes, loading.fetchQuoteById
 *   - error.fetchOrders, error.fetchOrderById, error.fetchOrderTracking,
 *     error.fetchQuotes, error.fetchQuoteById
 *
 * Thunks
 * ------
 * Five `createAsyncThunk` actions, one per endpoint:
 *  - fetchOrders(params?) — GET /account/orders with optional filters
 *  - fetchOrderById(id)   — GET /account/orders/:id
 *  - fetchOrderTracking(orderId) — GET /orders/:id/tracking
 *  - fetchQuotes(params?) — GET /account/quotes with optional filters
 *  - fetchQuoteById(id)   — GET /account/quotes/:id
 *
 * Architecture: this slice lives in @dfq/shared/stores so both the
 * Storefront and Console (read-only view) can consume it. Containers
 * dispatch thunks and read selectors; capsules receive plain props.
 *
 * SF-ORD-001.
 */

import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import type { ApiErrorSeverity } from '@dfq/shared/api/_envelope';
import { isApiError } from '@dfq/shared/api/_envelope';
import * as ordersApiModule from '@dfq/shared/api/orders';
import type {
  AcceptQuoteResponseData,
  CreateQuoteRequestPayload,
  DamageClaimDTO,
  GetOrdersParams,
  GetQuotesParams,
  OrderDTO,
  OrderStatus,
  OrderTrackingDTO,
  QuoteRequestDTO,
} from '@dfq/shared/api/orders';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export enums + types so consumers only need one import path.
// ─────────────────────────────────────────────────────────────────────────────

export {
  OrderStatusEnum,
  QuoteStatusEnum,
} from '@dfq/shared/api/orders';
export type {
  OrderStatus,
  QuoteStatus,
  OrderDTO,
  OrderItemDTO,
  ArtworkFileDTO,
  AttachedFileDTO,
  OrderStatusHistoryDTO,
  ShipmentDTO,
  OrderDiscountDTO,
  QuoteRequestDTO,
  QuoteDTO,
  OrderTrackingDTO,
  ProductSnapshot,
  MaterialSnapshot,
  OptionsSnapshot,
  VehicleSnapshot,
  DimensionsSnapshot,
  AddOnSnapshot,
  ProductionJobSummaryDTO,
  CancelOrderResponseDTO,
  DamageClaimDTO,
  CreateQuoteRequestPayload,
  AcceptQuoteResponseData,
} from '@dfq/shared/api/orders';

// ─────────────────────────────────────────────────────────────────────────────
// Pagination metadata (same shape for orders and quotes)
// ─────────────────────────────────────────────────────────────────────────────

export interface PaginationMeta {
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-operation loading + error isolation
// ─────────────────────────────────────────────────────────────────────────────

export type OrdersLoadingKey =
  | 'fetchOrders'
  | 'fetchOrderById'
  | 'fetchOrderTracking'
  | 'fetchQuotes'
  | 'fetchQuoteById'
  | 'cancelOrder'
  | 'fileDamageClaim'
  | 'createQuoteRequest'
  | 'acceptQuote'
  | 'declineQuote';

export type LoadingState = Record<OrdersLoadingKey, boolean>;

/**
 * Per-operation error shape. `fetchOrderById` and `cancelOrder` use a
 * structured error so the container can redirect on HTTP 401/404 (for detail)
 * or surface the envelope message inline on 409 (for cancellation).
 * All other operations use a plain nullable string for simplicity.
 */
export interface OrderDetailError {
  /** HTTP status code from the platform response (or 0 for network failures). */
  status: number;
  /** Human-readable message from the platform envelope. */
  message: string;
  /** The envelope's `severity`, when the failure came from `ApiError` (e.g. `acceptQuote`'s decline/409/422 paths). */
  severity?: ApiErrorSeverity;
  /** The envelope's field-keyed `errors` map, when present (e.g. a 422 validation failure). */
  errors?: Record<string, string[]>;
}

export type ErrorState = {
  fetchOrders: string | null;
  fetchOrderById: OrderDetailError | null;
  fetchOrderTracking: string | null;
  fetchQuotes: string | null;
  fetchQuoteById: string | null;
  /** Structured error from cancelOrder — status 409 surfaces the envelope message inline. */
  cancelOrder: OrderDetailError | null;
  /** Structured error from fileDamageClaim — 422 surfaces per-field errors inline. */
  fileDamageClaim: OrderDetailError | null;
  createQuoteRequest: OrderDetailError | null;
  acceptQuote: OrderDetailError | null;
  declineQuote: string | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Entity adapter for orders.list (O(1) selectById)
// ─────────────────────────────────────────────────────────────────────────────

const ordersAdapter = createEntityAdapter<OrderDTO>();

// ─────────────────────────────────────────────────────────────────────────────
// State shape
// ─────────────────────────────────────────────────────────────────────────────

export interface OrdersState {
  orders: {
    list: ReturnType<typeof ordersAdapter.getInitialState>;
    pagination: PaginationMeta | null;
    selectedOrder: OrderDTO | null;
    tracking: Record<number, OrderTrackingDTO>;
  };
  quotes: {
    list: QuoteRequestDTO[];
    pagination: PaginationMeta | null;
    selectedQuote: QuoteRequestDTO | null;
  };
  damageClaims: {
    /** Most-recently filed claim — set on `fileDamageClaim.fulfilled`. */
    lastFiled: DamageClaimDTO | null;
  };
  loading: LoadingState;
  error: ErrorState;
}

const INITIAL_LOADING: LoadingState = {
  fetchOrders: false,
  fetchOrderById: false,
  fetchOrderTracking: false,
  fetchQuotes: false,
  fetchQuoteById: false,
  cancelOrder: false,
  fileDamageClaim: false,
  createQuoteRequest: false,
  acceptQuote: false,
  declineQuote: false,
};

const INITIAL_ERROR: ErrorState = {
  fetchOrders: null,
  fetchOrderById: null,
  fetchOrderTracking: null,
  fetchQuotes: null,
  fetchQuoteById: null,
  cancelOrder: null,
  fileDamageClaim: null,
  createQuoteRequest: null,
  acceptQuote: null,
  declineQuote: null,
};

const initialState: OrdersState = {
  orders: {
    list: ordersAdapter.getInitialState(),
    pagination: null,
    selectedOrder: null,
    tracking: {},
  },
  quotes: {
    list: [],
    pagination: null,
    selectedQuote: null,
  },
  damageClaims: {
    lastFiled: null,
  },
  loading: INITIAL_LOADING,
  error: INITIAL_ERROR,
};

// ─────────────────────────────────────────────────────────────────────────────
// Async thunks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of the authenticated customer's orders.
 * Accepts optional filter + pagination params. The arg is typed as
 * `GetOrdersParams | void` so `dispatch(fetchOrders())` compiles without
 * an explicit argument — the thunk unwraps `void` as `{}`.
 */
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params: GetOrdersParams | void) => {
    return ordersApiModule.getOrders(params ?? {});
  },
);

/**
 * Fetch a single order with all nested relations (items, status history,
 * discounts, shipments).
 *
 * Uses `rejectWithValue` so the rejected action payload carries
 * `{ status, message }` — enabling the container to redirect on 401/404
 * without substring-matching a free-text message string.
 */
export const fetchOrderById = createAsyncThunk(
  'orders/fetchOrderById',
  async (id: number, { rejectWithValue }) => {
    try {
      return await ordersApiModule.getOrderById(id);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ status: err.status, message: err.message } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to fetch order';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

/**
 * Fetch real-time carrier tracking data for a given order. The result is
 * stored keyed by order id so tracking data for multiple orders is cached
 * independently.
 */
export const fetchOrderTracking = createAsyncThunk(
  'orders/fetchOrderTracking',
  async (orderId: number) => {
    return ordersApiModule.getOrderTracking(orderId);
  },
);

/**
 * Fetch a paginated list of the authenticated customer's quote requests.
 * Accepts optional `status` filter and pagination params. The arg is typed
 * as `GetQuotesParams | void` so `dispatch(fetchQuotes())` compiles without
 * an explicit argument.
 */
export const fetchQuotes = createAsyncThunk(
  'orders/fetchQuotes',
  async (params: GetQuotesParams | void) => {
    return ordersApiModule.getQuotes(params ?? {});
  },
);

/**
 * Fetch a single quote request with its full detail (files, admin notes,
 * and the nested `QuoteDTO` with line items if one exists).
 */
export const fetchQuoteById = createAsyncThunk(
  'orders/fetchQuoteById',
  async (id: number) => {
    return ordersApiModule.getQuoteById(id);
  },
);

/**
 * Cancel an order on behalf of the authenticated customer.
 *
 * Allowed statuses: `pending_payment`, `payment_confirmed`, `in_prepress`,
 * `artwork_issue`, `approved_for_print`. Returns a structured error on 409
 * (production already started) so the container can surface it inline
 * without a generic toast.
 *
 * Backend: PL-FULFILLMENT-010 (`CancelOrderController`).
 * SF-FULFILLMENT-002.
 */
export const cancelOrder = createAsyncThunk(
  'orders/cancelOrder',
  async (
    { orderId, reason }: { orderId: number; reason?: string },
    { rejectWithValue },
  ) => {
    try {
      return await ordersApiModule.cancelOrder(orderId, reason);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ status: err.status, message: err.message } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to cancel order';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

/**
 * File a shipping damage claim for an order.
 *
 * Submits `shipment_id`, `description`, and up to 5 photo `File` objects as
 * `multipart/form-data`. Returns the newly-created `DamageClaimDTO` on
 * success; uses `rejectWithValue` so 422 validation errors and 403
 * ownership failures surface inline via `{ status, message }`.
 *
 * Backend: PL-FULFILLMENT-012 (`FileShippingDamageClaimController`).
 * SF-FULFILLMENT-003.
 */
export const fileDamageClaim = createAsyncThunk(
  'orders/fileDamageClaim',
  async (
    payload: {
      orderId: number;
      shipmentId: number;
      description: string;
      /** Object keys from a direct-to-S3 upload. Preferred. */
      photo_s3_keys?: string[];
      /** Raw files, multipart. Blocked at the edge above 8KB. */
      photos?: File[];
    },
    { rejectWithValue },
  ) => {
    try {
      return await ordersApiModule.fileDamageClaim(payload.orderId, {
        shipment_id: payload.shipmentId,
        description: payload.description,
        photo_s3_keys: payload.photo_s3_keys,
        photos: payload.photos,
      });
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ status: err.status, message: err.message } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to file damage claim';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

/**
 * Submit a new customer quote request.
 */
export const submitQuoteRequest = createAsyncThunk(
  'orders/createQuoteRequest',
  async (payload: CreateQuoteRequestPayload, { rejectWithValue }) => {
    try {
      return await ordersApiModule.createQuoteRequest(payload);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ status: err.status, message: err.message } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to submit quote request';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

/**
 * Accept a sent quote by charging `paymentMethodId` for the quote's
 * `amountDueBasis` amount (PL-WRAP-112). Returns the accepted quote +
 * the locked-price order, including any 3DS `clientSecret`/`requiresAction`
 * the caller must finish with Stripe.js.
 */
export const acceptQuote = createAsyncThunk(
  'orders/acceptQuote',
  async (
    { id, paymentMethodId }: { id: number; paymentMethodId: string },
    { rejectWithValue },
  ) => {
    try {
      return await ordersApiModule.acceptQuote(id, paymentMethodId);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({
          status: err.status,
          message: err.message,
          severity: err.severity,
          errors: err.errors,
        } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to accept quote';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

/**
 * Decline a sent quote. Returns the updated quote request.
 */
export const declineQuote = createAsyncThunk(
  'orders/declineQuote',
  async ({ id, reason }: { id: number; reason?: string }, { rejectWithValue }) => {
    try {
      return await ordersApiModule.declineQuote(id, reason);
    } catch (err) {
      if (isApiError(err)) {
        return rejectWithValue({ status: err.status, message: err.message } satisfies OrderDetailError);
      }
      const msg = err instanceof Error ? err.message : 'Failed to decline quote';
      return rejectWithValue({ status: 0, message: msg } satisfies OrderDetailError);
    }
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────

export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ── fetchOrders ────────────────────────────────────────────────────────
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading.fetchOrders = true;
        state.error.fetchOrders = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading.fetchOrders = false;
        const { data, meta } = action.payload;
        ordersAdapter.setAll(state.orders.list, data);
        state.orders.pagination = {
          page: meta.current_page,
          perPage: meta.per_page,
          total: meta.total,
          lastPage: meta.last_page,
        };
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading.fetchOrders = false;
        state.error.fetchOrders = action.error.message ?? 'Failed to fetch orders';
      });

    // ── fetchOrderById ─────────────────────────────────────────────────────
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.loading.fetchOrderById = true;
        state.error.fetchOrderById = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading.fetchOrderById = false;
        state.orders.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading.fetchOrderById = false;
        // `fetchOrderById` uses `rejectWithValue` so the structured payload
        // always carries `{ status, message }`. Fall back defensively for
        // any future code path that bypasses `rejectWithValue`.
        if (action.payload != null) {
          state.error.fetchOrderById = action.payload as OrderDetailError;
        } else {
          state.error.fetchOrderById = {
            status: 0,
            message: action.error.message ?? 'Failed to fetch order',
          };
        }
      });

    // ── fetchOrderTracking ─────────────────────────────────────────────────
    builder
      .addCase(fetchOrderTracking.pending, (state) => {
        state.loading.fetchOrderTracking = true;
        state.error.fetchOrderTracking = null;
      })
      .addCase(fetchOrderTracking.fulfilled, (state, action) => {
        state.loading.fetchOrderTracking = false;
        // Key by the id that was requested, not one read off the payload: the
        // endpoint returns `{ shipments, aggregateStatus }` and never an
        // order id, so every result was landing under `undefined` and no
        // consumer ever saw it.
        state.orders.tracking[action.meta.arg] = action.payload;
      })
      .addCase(fetchOrderTracking.rejected, (state, action) => {
        state.loading.fetchOrderTracking = false;
        state.error.fetchOrderTracking = action.error.message ?? 'Failed to fetch tracking';
      });

    // ── fetchQuotes ────────────────────────────────────────────────────────
    builder
      .addCase(fetchQuotes.pending, (state) => {
        state.loading.fetchQuotes = true;
        state.error.fetchQuotes = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.loading.fetchQuotes = false;
        const { data, meta } = action.payload;
        state.quotes.list = data;
        state.quotes.pagination = {
          page: meta.current_page,
          perPage: meta.per_page,
          total: meta.total,
          lastPage: meta.last_page,
        };
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.loading.fetchQuotes = false;
        state.error.fetchQuotes = action.error.message ?? 'Failed to fetch quotes';
      });

    // ── fetchQuoteById ─────────────────────────────────────────────────────
    builder
      .addCase(fetchQuoteById.pending, (state) => {
        state.loading.fetchQuoteById = true;
        state.error.fetchQuoteById = null;
      })
      .addCase(fetchQuoteById.fulfilled, (state, action) => {
        state.loading.fetchQuoteById = false;
        state.quotes.selectedQuote = action.payload;
      })
      .addCase(fetchQuoteById.rejected, (state, action) => {
        state.loading.fetchQuoteById = false;
        state.error.fetchQuoteById = action.error.message ?? 'Failed to fetch quote';
      });

    // ── cancelOrder ────────────────────────────────────────────────────────
    builder
      .addCase(cancelOrder.pending, (state) => {
        state.loading.cancelOrder = true;
        state.error.cancelOrder = null;
      })
      .addCase(cancelOrder.fulfilled, (state) => {
        state.loading.cancelOrder = false;
        state.error.cancelOrder = null;
        // Update the selectedOrder status to 'cancelled' for immediate UI
        // reflection without a full re-fetch. The view navigates to a
        // thank-you state on success (the container handles navigation).
        if (state.orders.selectedOrder) {
          state.orders.selectedOrder = {
            ...state.orders.selectedOrder,
            status: 'cancelled' as OrderStatus,
          };
        }
      })
      .addCase(cancelOrder.rejected, (state, action) => {
        state.loading.cancelOrder = false;
        // `cancelOrder` uses `rejectWithValue` so the structured payload
        // always carries `{ status, message }` for inline display.
        if (action.payload != null) {
          state.error.cancelOrder = action.payload as OrderDetailError;
        } else {
          state.error.cancelOrder = {
            status: 0,
            message: action.error.message ?? 'Failed to cancel order',
          };
        }
      });

    // ── fileDamageClaim ────────────────────────────────────────────────────
    builder
      .addCase(fileDamageClaim.pending, (state) => {
        state.loading.fileDamageClaim = true;
        state.error.fileDamageClaim = null;
      })
      .addCase(fileDamageClaim.fulfilled, (state, action) => {
        state.loading.fileDamageClaim = false;
        state.error.fileDamageClaim = null;
        state.damageClaims.lastFiled = action.payload;
      })
      .addCase(fileDamageClaim.rejected, (state, action) => {
        state.loading.fileDamageClaim = false;
        if (action.payload != null) {
          state.error.fileDamageClaim = action.payload as OrderDetailError;
        } else {
          state.error.fileDamageClaim = {
            status: 0,
            message: action.error.message ?? 'Failed to file damage claim',
          };
        }
      });

    // ── submitQuoteRequest ─────────────────────────────────────────────────
    builder
      .addCase(submitQuoteRequest.pending, (state) => {
        state.loading.createQuoteRequest = true;
        state.error.createQuoteRequest = null;
      })
      .addCase(submitQuoteRequest.fulfilled, (state, action) => {
        state.loading.createQuoteRequest = false;
        state.quotes.list = [action.payload, ...state.quotes.list];
      })
      .addCase(submitQuoteRequest.rejected, (state, action) => {
        state.loading.createQuoteRequest = false;
        if (action.payload != null) {
          state.error.createQuoteRequest = action.payload as OrderDetailError;
        } else {
          state.error.createQuoteRequest = {
            status: 0,
            message: action.error.message ?? 'Failed to submit quote request',
          };
        }
      });

    // ── acceptQuote ────────────────────────────────────────────────────────
    builder
      .addCase(acceptQuote.pending, (state) => {
        state.loading.acceptQuote = true;
        state.error.acceptQuote = null;
      })
      .addCase(acceptQuote.fulfilled, (state, action) => {
        state.loading.acceptQuote = false;
        const { quote, order } = action.payload as AcceptQuoteResponseData;
        // Unresolved 3DS — stay `quoted` so the payment panel isn't hidden mid-challenge.
        if (order.requiresAction) return;

        state.quotes.list = state.quotes.list.map((q) =>
          q.id === quote.quoteRequestId ? { ...q, status: 'accepted' as const } : q,
        );
        if (state.quotes.selectedQuote?.id === quote.quoteRequestId) {
          state.quotes.selectedQuote = { ...state.quotes.selectedQuote, status: 'accepted' as const };
        }
      })
      .addCase(acceptQuote.rejected, (state, action) => {
        state.loading.acceptQuote = false;
        if (action.payload != null) {
          state.error.acceptQuote = action.payload as OrderDetailError;
        } else {
          state.error.acceptQuote = {
            status: 0,
            message: action.error.message ?? 'Failed to accept quote',
          };
        }
      });

    // ── declineQuote ───────────────────────────────────────────────────────
    builder
      .addCase(declineQuote.pending, (state) => {
        state.loading.declineQuote = true;
        state.error.declineQuote = null;
      })
      .addCase(declineQuote.fulfilled, (state, action) => {
        state.loading.declineQuote = false;
        const updated = action.payload;
        state.quotes.list = state.quotes.list.map((q) =>
          q.id === updated.id ? updated : q,
        );
        if (state.quotes.selectedQuote?.id === updated.id) {
          state.quotes.selectedQuote = updated;
        }
      })
      .addCase(declineQuote.rejected, (state, action) => {
        state.loading.declineQuote = false;
        // `declineQuote` rejects via `rejectWithValue`, so the API's actual
        // envelope message lives on `action.payload`, not `action.error`
        // (RTK sets that to a generic "Rejected" for a rejectWithValue path).
        const payload = action.payload as OrderDetailError | undefined;
        state.error.declineQuote =
          payload?.message ?? action.error.message ?? 'Failed to decline quote';
      });
  },
});

export const ordersReducer = ordersSlice.reducer;

// ─────────────────────────────────────────────────────────────────────────────
// Selectors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Minimal root-state shape that the selectors require. Sliced to the
 * `orders` key so consumers can extend the type for their own app-level
 * state without breaking the selectors.
 */
export interface OrdersRootState {
  orders: OrdersState;
}

const ordersEntitySelectors = ordersAdapter.getSelectors(
  (state: OrdersRootState) => state.orders.orders.list,
);

/**
 * Returns the list of orders from the entity adapter, in insertion order.
 * Includes pagination metadata as a side-car object.
 */
export const selectOrders = createSelector(
  (state: OrdersRootState) => ordersEntitySelectors.selectAll(state),
  (state: OrdersRootState) => state.orders.orders.pagination,
  (list, pagination) => ({ list, pagination }),
);

/**
 * Returns the currently-selected full-detail order (populated after
 * `fetchOrderById` resolves), or `null` when none is loaded.
 */
export const selectOrderById = (state: OrdersRootState): OrderDTO | null =>
  state.orders.orders.selectedOrder;

/**
 * Returns the tracking data for a given order id, or `undefined` when not
 * yet fetched. Callers check for `undefined` to decide whether to dispatch
 * `fetchOrderTracking`.
 */
export const selectOrderTracking = (
  state: OrdersRootState,
  orderId: number,
): OrderTrackingDTO | undefined => state.orders.orders.tracking[orderId];

/**
 * Returns the quote list with its pagination metadata.
 */
export const selectQuotes = createSelector(
  (state: OrdersRootState) => state.orders.quotes.list,
  (state: OrdersRootState) => state.orders.quotes.pagination,
  (list, pagination) => ({ list, pagination }),
);

/**
 * Returns the currently-selected full-detail quote request (populated after
 * `fetchQuoteById` resolves), or `null` when none is loaded.
 */
export const selectQuoteById = (state: OrdersRootState): QuoteRequestDTO | null =>
  state.orders.quotes.selectedQuote;

/**
 * Returns the full per-operation loading map. Consumers can read a specific
 * key (e.g. `selectOrdersLoading(state).fetchOrders`) for targeted loading
 * states without coupling to sibling operations.
 */
export const selectOrdersLoading = (state: OrdersRootState): LoadingState =>
  state.orders.loading;

/**
 * Returns the full per-operation error map.
 */
export const selectOrdersError = (state: OrdersRootState): ErrorState =>
  state.orders.error;

/**
 * Returns the cancellation error for the current order, or null when no
 * error has occurred. The container uses this to surface 409 messages inline
 * inside the cancellation dialog.
 */
export const selectCancelOrderError = (
  state: OrdersRootState,
): OrderDetailError | null => state.orders.error.cancelOrder;

/**
 * Returns true when a cancelOrder operation is in flight.
 */
export const selectCancelOrderLoading = (state: OrdersRootState): boolean =>
  state.orders.loading.cancelOrder;

/**
 * Returns the fileDamageClaim error for the current operation, or null when no
 * error has occurred. The container uses this to surface 422/403 messages inline
 * inside the damage claim dialog.
 */
export const selectFileDamageClaimError = (
  state: OrdersRootState,
): OrderDetailError | null => state.orders.error.fileDamageClaim;

/**
 * Returns true when a fileDamageClaim operation is in flight.
 */
export const selectFileDamageClaimLoading = (state: OrdersRootState): boolean =>
  state.orders.loading.fileDamageClaim;

/**
 * Returns the most-recently filed damage claim, or null when none has been
 * filed in the current session.
 */
export const selectLastFiledDamageClaim = (
  state: OrdersRootState,
): DamageClaimDTO | null => state.orders.damageClaims.lastFiled;

export const selectSubmitQuoteLoading = (state: OrdersRootState): boolean =>
  state.orders.loading.createQuoteRequest;

export const selectSubmitQuoteError = (state: OrdersRootState): OrderDetailError | null =>
  state.orders.error.createQuoteRequest;

export const selectAcceptQuoteLoading = (state: OrdersRootState): boolean =>
  state.orders.loading.acceptQuote;

export const selectAcceptQuoteError = (state: OrdersRootState): OrderDetailError | null =>
  state.orders.error.acceptQuote;

export const selectDeclineQuoteLoading = (state: OrdersRootState): boolean =>
  state.orders.loading.declineQuote;

export const selectDeclineQuoteError = (state: OrdersRootState): string | null =>
  state.orders.error.declineQuote;
